import { httpClient } from '@/core/api/httpClient';
import type {
  Project,
  ProjectDtoPagedResult,
  ProjectsQueryParams,
  CreateProjectPayload,
  UpdateProjectPayload,
  ProjectSummary,
  MyProjectDto,
  MyProjectDtoPagedResult,
} from '../types';
import { mockProjects } from './projects.mock';
import { env } from '@/core/config/env';
import { API_V1 } from '@/core/api/endpoints';

const USE_MOCK = env.VITE_USE_MOCK === 'true';

function adaptMyProjectDto(dto: MyProjectDto): Project {
  return {
    id: dto.id ?? '',
    name: dto.name,
    description: dto.description,
    status: dto.status,
    isArchived: dto.isArchived,
    color: dto.color,
    icon: null,
    clientId: dto.clientId,
    tenantId: dto.tenantId,
    startDate: dto.startDate,
    endDate: dto.endDate,
    createdAt: dto.createdAt,
    updatedAt: null,
  };
}

const buildMockPagedResult = (
  items: Project[],
  params: ProjectsQueryParams,
): ProjectDtoPagedResult => {
  const page = params.Page ?? 1;
  const pageSize = params.PageSize ?? 20;
  const filtered = params.IncludeArchived ? items : items.filter((p) => !p.isArchived);
  const total = filtered.length;
  const totalPages = Math.max(1, Math.ceil(total / pageSize));
  const start = (page - 1) * pageSize;
  const pageItems = filtered.slice(start, start + pageSize);

  return {
    items: pageItems,
    page,
    pageSize,
    totalCount: total,
    totalPages,
    hasPreviousPage: page > 1,
    hasNextPage: page < totalPages,
    firstItemOnPage: start + 1,
    lastItemOnPage: Math.min(start + pageSize, total),
  };
};

export const projectsService = {
  /**
   * Returns a paged list of projects for the active tenant.
   * GET /api/projects
   */
  getList: async (
    params?: ProjectsQueryParams,
    signal?: AbortSignal,
  ): Promise<ProjectDtoPagedResult> => {
    if (USE_MOCK) return buildMockPagedResult(mockProjects, params ?? {});
    const { data } = await httpClient.get<ProjectDtoPagedResult>(`${API_V1}/projects`, {
      params,
      signal,
    });
    return data;
  },

  /**
   * Returns a single project by ID.
   * GET /api/projects/{projectId}
   */
  getById: async (projectId: string, signal?: AbortSignal): Promise<Project> => {
    if (USE_MOCK) {
      const project = mockProjects.find((p) => p.id === projectId);
      if (!project) throw new Error('Project not found');
      return project;
    }
    const url = `${API_V1}/projects/${projectId}`;
    const { data } = await (signal
      ? httpClient.get<Project>(url, { signal })
      : httpClient.get<Project>(url));
    return data;
  },

  /**
   * Creates a new project for the active tenant.
   * POST /api/projects → 201 No body
   */
  create: async (payload: CreateProjectPayload): Promise<void> => {
    if (USE_MOCK) return;
    await httpClient.post<CreateProjectPayload, void>(`${API_V1}/projects`, payload);
  },

  /**
   * Updates an existing project.
   * PUT /api/projects/{projectId} → 204 No body
   */
  update: async (projectId: string, payload: UpdateProjectPayload): Promise<void> => {
    if (USE_MOCK) return;
    await httpClient.put<UpdateProjectPayload, void>(`${API_V1}/projects/${projectId}`, payload);
  },

  /**
   * Deletes a project.
   * DELETE /api/projects/{projectId} → 204 No body
   */
  delete: async (projectId: string): Promise<void> => {
    if (USE_MOCK) return;
    await httpClient.delete(`${API_V1}/projects/${projectId}`);
  },

  /**
   * Returns a summary of a project (member count, task count, status, dates).
   * GET /api/projects/{projectId}/summary
   */
  getSummary: async (projectId: string, signal?: AbortSignal): Promise<ProjectSummary> => {
    if (USE_MOCK) {
      const project = mockProjects.find((p) => p.id === projectId);
      return {
        id: projectId,
        name: project?.name ?? null,
        description: project?.description ?? null,
        status: project?.status ?? null,
        color: project?.color ?? null,
        avatarUrl: null,
        membersCount: 0,
        tasksCount: 0,
        startDate: project?.startDate ?? null,
        endDate: project?.endDate ?? null,
      };
    }
    const url = `${API_V1}/projects/${projectId}/summary`;
    const { data } = await (signal
      ? httpClient.get<ProjectSummary>(url, { signal })
      : httpClient.get<ProjectSummary>(url));
    return data;
  },

  /**
   * Returns the projects the current user is a member of in the active tenant.
   * GET /api/me/tenant/projects — response adapted to Project for sidebar compatibility.
   */
  getMyProjects: async (
    params?: ProjectsQueryParams,
    signal?: AbortSignal,
  ): Promise<ProjectDtoPagedResult> => {
    if (USE_MOCK) return buildMockPagedResult(mockProjects, params ?? {});
    const { data } = await httpClient.get<MyProjectDtoPagedResult>(`${API_V1}/me/tenant/projects`, {
      params,
      signal,
    });
    return {
      ...data,
      items: data.items?.map(adaptMyProjectDto) ?? null,
    };
  },

  uploadAvatar: (projectId: string, file: File) => {
    const formData = new FormData();
    formData.append('file', file);
    return httpClient.patch<FormData, void>(`${API_V1}/projects/${projectId}/avatar`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
  },

  deleteAvatar: (projectId: string) =>
    httpClient.delete<void>(`${API_V1}/projects/${projectId}/avatar`),
};
